import { UI_CONSTANTS } from "../../../Constants/constants";
import { FoodVolumetricsReviewError } from "../../../Interfaces/IFoodVolumetricsReviewType";
import {
  ICustomValidation,
  IDecimalValidation,
  IRequiredValidation,
  IValidationRule,
} from "../../../Interfaces/IValidationRule";
import {
  ERROR_MESSAGES,
  FIELDS,
  IProductFieldMetaData,
} from "../../../Interfaces/Product/IProductFormField";
import { IProductInputRangeValidation } from "../../../Interfaces/Product/IProductValidation";
import {
  ProductError,
  productError,
} from "../../../Types/Product/ProductError";
import { ProductValueType } from "../../../Types/Product/ProductValueType";
import { VolumetricsReviewDataModel } from "../../../Types/Product/VolumetricsReviewDataModel";

const OUTER_DIMENSIONS = "outerDimensions";
const INNER_DIMENSIONS = "innerDimensions";

const FIELDS_TO_VALIDATE = {
  Measurements: [OUTER_DIMENSIONS, INNER_DIMENSIONS],
  Attributes: [
    FIELDS.nestIncrement.name,
    FIELDS.automationToteFill.name,
    FIELDS.automationMinTotes.name,
  ],
};

export const IsNullEmptyUndefined = (value: any) => {
  if (typeof value === "string") {
    return value?.trim() === "";
  }

  return value === undefined || value === null;
};

const required = (
  value: any,
  formValues: any,
  field: string,
  ruleConfig?: IRequiredValidation,
  siblingPackValues?: any
) => {
  if (ruleConfig?.validateOn != undefined) {
    if (!ruleConfig.validateOn(value, formValues)) {
      return { valid: true, errorMessage: "" };
    }
  }
  const isValid = !IsNullEmptyUndefined(value);
  return {
    valid: isValid,
    errorMessage: isValid ? "" : ERROR_MESSAGES.required,
  };
};

const decimal = (
  value: any,
  formValues: any,
  field: string,
  ruleConfig?: IDecimalValidation,
  siblingPackValues?: any
) => {
  if (ruleConfig?.validateOn != undefined) {
    if (!ruleConfig.validateOn(value, formValues)) {
      return { valid: true, errorMessage: "" };
    }
  }

  if (IsNullEmptyUndefined(value)) {
    return { valid: true, errorMessage: "" };
  }

  const isValid = RegExp("^[+-]?[0-9]\\d*(\\.\\d+)?$").test(value);
  return {
    valid: isValid,
    errorMessage: isValid ? "" : ERROR_MESSAGES.decimal,
  };
};

const integer = (
  value: any,
  formValues: ProductValueType,
  field: string,
  ruleConfig?: IValidationRule,
  siblingPackValues?: any
) => {
  if (ruleConfig?.validateOn != undefined) {
    if (!ruleConfig.validateOn(value, formValues)) {
      return { valid: true, errorMessage: "" };
    }
  }

  if (IsNullEmptyUndefined(value)) {
    return { valid: true, errorMessage: "" };
  }

  const isValid = RegExp("^[+-]?[0-9]\\d*$").test(value);
  return {
    valid: isValid,
    errorMessage: isValid ? "" : ERROR_MESSAGES.integer(value),
  };
};

const inputRange = (
  value: any,
  formValues: ProductValueType,
  field: string,
  ruleConfig?: IProductInputRangeValidation,
  siblingPackValues?: any
) => {
  if (ruleConfig?.validateOn != undefined) {
    if (!ruleConfig.validateOn(value, formValues)) {
      return { valid: true, errorMessage: "" };
    }
  }

  if (IsNullEmptyUndefined(value)) {
    return { valid: true, errorMessage: "" };
  }

  let lowerLimit, upperLimit;
  let unit: string | undefined = "";

  switch (field) {
    case FIELDS.outerDimensions.length.name: {
      unit = formValues?.outerDimensions?.lengthUOM;
      break;
    }
    case FIELDS.outerDimensions.width.name: {
      unit = formValues?.outerDimensions?.widthUOM;
      break;
    }
    case FIELDS.outerDimensions.height.name: {
      unit = formValues?.outerDimensions?.heightUOM;
      break;
    }
    case FIELDS.innerDimensions.length.name: {
      unit = formValues?.innerDimensions?.lengthUOM;
      break;
    }
    case FIELDS.innerDimensions.width.name: {
      unit = formValues?.innerDimensions?.widthUOM;
      break;
    }
    case FIELDS.innerDimensions.height.name: {
      unit = formValues?.innerDimensions?.heightUOM;
      break;
    }
    case FIELDS.outerDimensions.weight.name: {
      unit = formValues?.outerDimensions?.weightUOM;
      break;
    }
    case FIELDS.innerDimensions.weight.name: {
      unit = formValues?.innerDimensions?.weightUOM;
      break;
    }
  }

  switch (unit) {
    case "CM": {
      lowerLimit = ruleConfig?.lowerLimitCM;
      upperLimit = ruleConfig?.upperLimitCM;
      break;
    }
    case "M": {
      lowerLimit = ruleConfig?.lowerLimitM;
      upperLimit = ruleConfig?.upperLimitM;
      break;
    }
    case "G": {
      lowerLimit = ruleConfig?.lowerLimitG;
      upperLimit = ruleConfig?.upperLimitG;
      break;
    }
    case "KG": {
      lowerLimit = ruleConfig?.lowerLimitKG;
      upperLimit = ruleConfig?.upperLimitKG;
      break;
    }
    default: {
      lowerLimit = ruleConfig?.lowerLimit;
      upperLimit = ruleConfig?.upperLimit;
      break;
    }
  }

  switch (ruleConfig?.comparision) {
    case "greater": {
      if (lowerLimit !== undefined) {
        if (value > lowerLimit) {
          return { valid: true, errorMessage: "" };
        } else {
          return {
            valid: false,
            errorMessage: ERROR_MESSAGES.inputRange(
              lowerLimit,
              upperLimit,
              unit,
              ruleConfig.comparision
            ),
          };
        }
      }
      break;
    }
    default: {
      if (lowerLimit !== undefined && upperLimit !== undefined) {
        if (value >= lowerLimit && value <= upperLimit) {
          return { valid: true, errorMessage: "" };
        } else {
          return {
            valid: false,
            errorMessage: ERROR_MESSAGES.inputRange(
              lowerLimit,
              upperLimit,
              unit
            ),
          };
        }
      }
    }
  }

  return { valid: true, errorMessage: "" };
};

const ConvertFieldValues = (
  leftValue: number | undefined,
  rightValue: number | undefined,
  leftUnit: string | undefined,
  rightUnit: string | undefined,
  targetUnit: string | undefined,
  conversionFuc: any
) => {
  if (leftUnit !== rightUnit) {
    if (leftUnit === targetUnit) {
      leftValue = conversionFuc(leftValue);
    } else if (rightUnit === targetUnit) {
      rightValue = conversionFuc(rightValue);
    }
  }

  return [(leftValue as number) * 1, (rightValue as number) * 1];
};

const MtoCM = (input: number) => input * 100;
const KGtoG = (input: number) => input * 1000;

const compareDimensions = (
  value: number | undefined,
  compareValue: number | undefined,
  valueUOM: string | undefined,
  compareUOM: string | undefined,
  targetUOM: string | undefined,
  conversionFunc: (input: number) => number,
  errorMessage: string | undefined,
  comparison: "gt" | "ls" | "gteq" | "lseq" = "gt"
) => {
  if (IsNullEmptyUndefined(compareValue)) {
    return { valid: true, errorMessage: "" };
  }

  const [leftValue, rightValue] = ConvertFieldValues(
    value as unknown as never,
    compareValue,
    valueUOM,
    compareUOM,
    targetUOM,
    conversionFunc
  );

  if (
    (comparison === "gt" && leftValue > rightValue) ||
    (comparison === "ls" && leftValue < rightValue) ||
    (comparison === "gteq" && leftValue >= rightValue) ||
    (comparison === "lseq" && leftValue <= rightValue)
  ) {
    return { valid: true, errorMessage };
  }

  return { valid: false, errorMessage: errorMessage };
};

const customSainsburys = (
  value: any,
  formValues: ProductValueType,
  field: string,
  ruleConfig?: ICustomValidation,
  siblingPackValues?: any
) => {
  if (
    !(
      formValues.alias === UI_CONSTANTS.SAINSBURYS_SKU ||
      formValues.alias === UI_CONSTANTS.SAINSBURYS_PACK
    )
  ) {
    return { valid: true, errorMessage: "" };
  }

  if (ruleConfig?.validateOn != undefined) {
    if (!ruleConfig.validateOn(value, formValues)) {
      return { valid: true, errorMessage: "" };
    }
  }

  if (IsNullEmptyUndefined(value)) {
    return { valid: true, errorMessage: "" };
  }

  switch (field) {
    case FIELDS.outerDimensions.height.name:
      return compareDimensions(
        value,
        formValues?.innerDimensions?.height,
        formValues?.outerDimensions?.heightUOM,
        formValues?.innerDimensions?.heightUOM,
        "M",
        MtoCM,
        ERROR_MESSAGES.breakpackHeightGteqCaseHeight
      );

    case FIELDS.outerDimensions.length.name:
      return compareDimensions(
        value,
        formValues?.innerDimensions?.length,
        formValues?.outerDimensions?.lengthUOM,
        formValues?.innerDimensions?.lengthUOM,
        "M",
        MtoCM,
        ERROR_MESSAGES.breakpackLengthGteqCaseLength
      );

    case FIELDS.outerDimensions.width.name: {
      const widthValidation = compareDimensions(
        value,
        formValues?.innerDimensions?.width,
        formValues?.outerDimensions?.widthUOM,
        formValues?.innerDimensions?.widthUOM,
        "M",
        MtoCM,
        ERROR_MESSAGES.breakpackWidthGteqCaseWidth
      );
      if (!widthValidation.valid) return widthValidation;

      return compareDimensions(
        value,
        formValues?.outerDimensions?.length,
        formValues?.outerDimensions?.widthUOM,
        formValues?.outerDimensions?.lengthUOM,
        "M",
        MtoCM,
        ERROR_MESSAGES.breakpackWidthLteqBreakpackLength,
        "lseq"
      );
    }

    case FIELDS.outerDimensions.weight.name:
      return compareDimensions(
        value,
        formValues?.innerDimensions?.weight,
        formValues?.outerDimensions?.weightUOM,
        formValues?.innerDimensions?.weightUOM,
        "KG",
        KGtoG,
        ERROR_MESSAGES.breakpackWeightGteqCaseWeight
      );
  }

  return { valid: true, errorMessage: "" };
};

const customArgos = (
  value: any,
  formValues: ProductValueType,
  field: string,
  ruleConfig?: ICustomValidation,
  siblingPackValues?: any
) => {
  if (formValues.alias !== UI_CONSTANTS.ARGOS) {
    return { valid: true, errorMessage: "" };
  }

  if (ruleConfig?.validateOn != undefined) {
    if (!ruleConfig.validateOn(value, formValues)) {
      return { valid: true, errorMessage: "" };
    }
  }

  const isEach = formValues.packSize === 1;
  const compareType = "gteq";

  if (IsNullEmptyUndefined(value) || IsNullEmptyUndefined(siblingPackValues) || isEach) {
    return { valid: true, errorMessage: "" };
  }

  switch (field) {
    case FIELDS.innerDimensions.height.name:
      return compareDimensions(
        value,
        siblingPackValues.DimensionData.InnerDimensions.Height,
        formValues?.innerDimensions?.heightUOM,
        siblingPackValues.DimensionData.InnerDimensions.HeightUOM,
        "M",
        MtoCM,
        ERROR_MESSAGES.vendorGteqEachHeight(
          siblingPackValues.DimensionData.InnerDimensions.Height,
          siblingPackValues.DimensionData.InnerDimensions.HeightUOM
        ),
        compareType
      );

    case FIELDS.innerDimensions.length.name:
      return compareDimensions(
        value,
        siblingPackValues.DimensionData.InnerDimensions.Length,
        formValues?.innerDimensions?.lengthUOM,
        siblingPackValues.DimensionData.InnerDimensions.LengthUOM,
        "M",
        MtoCM,
        ERROR_MESSAGES.vendorGteqEachLength(
          siblingPackValues.DimensionData.InnerDimensions.Length,
          siblingPackValues.DimensionData.InnerDimensions.LengthUOM
        ),
        compareType
      );

    case FIELDS.innerDimensions.width.name: {
      return compareDimensions(
        value,
        siblingPackValues.DimensionData.InnerDimensions.Width,
        formValues?.innerDimensions?.widthUOM,
        siblingPackValues.DimensionData.InnerDimensions.WidthUOM,
        "M",
        MtoCM,
        ERROR_MESSAGES.vendorGteqEachWidth(
          siblingPackValues.DimensionData.InnerDimensions.Width,
          siblingPackValues.DimensionData.InnerDimensions.WidthUOM
        ),
        compareType
      );
    }

    case FIELDS.innerDimensions.weight.name:
      return compareDimensions(
        value,
        siblingPackValues.DimensionData.InnerDimensions.Weight,
        formValues?.innerDimensions?.weightUOM,
        siblingPackValues.DimensionData.InnerDimensions.WeightUOM,
        "KG",
        KGtoG,
        ERROR_MESSAGES.vendorGteqEachWeight(
          siblingPackValues.DimensionData.InnerDimensions.Weight,
          siblingPackValues.DimensionData.InnerDimensions.WeightUOM
        ),
        "gt"
      );
  }

  return { valid: true, errorMessage: "" };
};

const rules = {
  required,
  integer,
  decimal,
  inputRange,
  customSainsburys,
  customArgos,
};

const validateField = (
  field: string,
  value: any,
  formValues: any,
  siblingPackValues?: any
) => {
  let result = true;
  let errors: any;
  const selectedField = FIELDS[field as keyof typeof FIELDS];
  const fieldNameArray = field.split("_");
  const isDisabled = document
    .querySelector(`form input[name=${field}]`)
    ?.hasAttribute("disabled");

  if (fieldNameArray.length === 1) {
    errors = { [field]: "" };

    if (isDisabled) {
      return { result, errors };
    }

    Object.keys((selectedField as IProductFieldMetaData).rules).forEach(
      (rule) => {
        if (!result) {
          return;
        }

        const fieldResult = rules[rule as keyof typeof rules](
          value,
          formValues,
          field,
          (selectedField as IProductFieldMetaData).rules[
            rule as keyof typeof rules
          ],
          siblingPackValues
        );

        if (!fieldResult.valid) {
          result = false;
          errors[field] = fieldResult.errorMessage;
          return { result, errors };
        }
      }
    );
  } else {
    const isOuterDimension = fieldNameArray[0] === OUTER_DIMENSIONS;
    let dimensionField: IProductFieldMetaData;

    if (isOuterDimension) {
      errors = { outerDimensions: { [fieldNameArray[1]]: "" } };

      if (isDisabled) {
        return { result, errors };
      }

      dimensionField =
        FIELDS.outerDimensions[
          fieldNameArray[1] as keyof typeof FIELDS.outerDimensions
        ];
    } else {
      errors = { innerDimensions: { [fieldNameArray[1]]: "" } };

      if (isDisabled) {
        return { result, errors };
      }

      dimensionField =
        FIELDS.innerDimensions[
          fieldNameArray[1] as keyof typeof FIELDS.innerDimensions
        ];
    }

    Object.keys(dimensionField.rules).forEach((rule) => {
      if (!result) {
        return;
      }

      const fieldResult = rules[rule as keyof typeof rules](
        value,
        formValues,
        field,
        dimensionField.rules[rule as keyof typeof rules],
        siblingPackValues
      );

      if (!fieldResult.valid) {
        result = false;

        if (isOuterDimension) {
          errors.outerDimensions[fieldNameArray[1]] = fieldResult.errorMessage;
        } else {
          errors.innerDimensions[fieldNameArray[1]] = fieldResult.errorMessage;
        }

        return { result, errors };
      }
    });
  }

  return { result, errors };
};

const appendErrorMessage = (currentMessage: string | undefined, newMessage: string): string => {
  return currentMessage
    ? `${currentMessage}\n${newMessage}`
    : newMessage;
};

const validateMandatoryDimension = (value: number | null | undefined, key: keyof FoodVolumetricsReviewError, errors: FoodVolumetricsReviewError): void => {
  if (!value) {
    errors[key] = ERROR_MESSAGES.required;
  }
};

const validateNonNegative = (value: number | null | undefined, key: keyof FoodVolumetricsReviewError, errors: FoodVolumetricsReviewError): void => {
  if (value && value < 0) {
    errors[key] = ERROR_MESSAGES.lessThanZero;
  }
};

export const validateFoodVolumetricsForm = (productVolumetrics: VolumetricsReviewDataModel): {
  isValid: boolean;
  errors: FoodVolumetricsReviewError;
} => {
  const { caseLengthMm, caseWidthMm, caseHeightMm, caseWeightKg, casesPerLayer, layersPerPallet } = productVolumetrics;

  const invalidDimensionErrorMessage: string = "Value must be greater than or equal to 1 MM and less than or equal to 10000 MM.";
  const invalidWeightErrorMessage: string = "Value must be greater than or equal to 0.1 KG and less than or equal to 2000 KG.";

  const errors: FoodVolumetricsReviewError = {
    caseLengthMm: undefined,
    caseWidthMm: undefined,
    caseHeightMm: undefined,
    caseWeightKg: undefined,
    casesPerLayer: undefined,
    layersPerPallet: undefined,
    casesPerPallet: undefined
  };

  validateMandatoryDimension(caseLengthMm, "caseLengthMm", errors);
  validateMandatoryDimension(caseWidthMm, "caseWidthMm", errors);
  validateMandatoryDimension(caseHeightMm, "caseHeightMm", errors);
  validateMandatoryDimension(caseWeightKg, "caseWeightKg", errors);

  if (caseLengthMm && (caseLengthMm <= 0 || caseLengthMm > 10000)) {
    errors.caseLengthMm = appendErrorMessage(errors.caseLengthMm, invalidDimensionErrorMessage);
  }

  if (caseWidthMm && (caseWidthMm <= 0 || caseWidthMm > 10000)) {
    errors.caseWidthMm = appendErrorMessage(errors.caseWidthMm, invalidDimensionErrorMessage);
  }

  if (caseHeightMm && (caseHeightMm <= 0 || caseHeightMm > 10000)) {
    errors.caseHeightMm = appendErrorMessage(errors.caseHeightMm, invalidDimensionErrorMessage);
  }

  if (caseWeightKg && (caseWeightKg <= 0 || caseWeightKg > 2000)) {
    errors.caseWeightKg = appendErrorMessage(errors.caseWeightKg, invalidWeightErrorMessage);
  }

  validateNonNegative(casesPerLayer, "casesPerLayer", errors);
  validateNonNegative(layersPerPallet, "layersPerPallet", errors);
  validateNonNegative((casesPerLayer && layersPerPallet) ? (casesPerLayer * layersPerPallet) : 0, "casesPerPallet", errors);

  return {
    isValid: !(!!errors.caseLengthMm || !!errors.caseWidthMm || !!errors.caseHeightMm || !!errors.caseWeightKg || !!errors.casesPerLayer || !!errors.layersPerPallet || !!errors.casesPerPallet),
    errors: errors
  };
}

export const validateForm = (
  values: ProductValueType,
  selectedMenu: string,
  siblingPackValues?: ProductValueType
) => {
  let result = true;
  let errors = JSON.parse(JSON.stringify(productError));
  let fieldArray;

  // As validations are available in Measurement screen as of now.
  if (selectedMenu !== "Measurements" && selectedMenu !== "Attributes") {
    return { result, errors };
  }

  fieldArray =
    FIELDS_TO_VALIDATE[selectedMenu as keyof typeof FIELDS_TO_VALIDATE];

  fieldArray.forEach((field) => {
    let fieldResult;

    if (field === OUTER_DIMENSIONS) {
      Object.values(FIELDS.outerDimensions).forEach((field) => {
        const propName = field.name.split("_");
        if (values.outerDimensions) {
          fieldResult = validateField(
            field.name,
            values.outerDimensions[
              propName[1] as keyof typeof FIELDS.outerDimensions
            ],
            values,
            siblingPackValues
          );
          if (!fieldResult.result) {
            result = false;
            errors.outerDimensions[
              propName[1] as keyof typeof FIELDS.outerDimensions
            ] = fieldResult.errors.outerDimensions[propName[1]];
          }
        }
      });
    } else if (field === INNER_DIMENSIONS) {
      Object.values(FIELDS.innerDimensions).forEach((field) => {
        const propName = field.name.split("_");
        if (values.innerDimensions) {
          fieldResult = validateField(
            field.name,
            values.innerDimensions[
              propName[1] as keyof typeof FIELDS.innerDimensions
            ],
            values,
            siblingPackValues
          );

          if (!fieldResult.result) {
            result = false;
            errors.innerDimensions[
              propName[1] as keyof typeof FIELDS.innerDimensions
            ] = fieldResult.errors.innerDimensions[propName[1]];
          }
        }
      });
    } else {
      fieldResult = validateField(
        field,
        values[field as keyof typeof FIELDS],
        values,
        siblingPackValues
      );
      if (!fieldResult.result) {
        result = false;
        errors[field as keyof typeof FIELDS] = fieldResult.errors[field];
      }
    }
  });

  return { result, errors };
};

// Validate Warnings
export const validateWarnings = (
  fieldName: string,
  values: ProductValueType,
  errors: ProductError
) => {
  let fieldValue: any;

  switch (fieldName) {
    case FIELDS.outerDimensions.length.name: {
      fieldValue = values.outerDimensions?.length;

      if (IsNullEmptyUndefined(fieldValue)) {
        return { valid: true, warningMessage: "" };
      }

      fieldValue = fieldValue * 1;

      if (values.outerDimensions?.lengthUOM === "CM") {
        if (fieldValue > 135) {
          return {
            valid: false,
            warningMessage:
              "You are about to save a breakpack length dimension that is greater than the pallet length (135 CM)",
          };
        }
      }

      if (values.outerDimensions?.lengthUOM === "M") {
        if (fieldValue > 1.35) {
          return {
            valid: false,
            warningMessage:
              "You are about to save a breakpack length dimension that is greater than the pallet length (1.35 M)",
          };
        }
      }

      break;
    }
    case FIELDS.outerDimensions.width.name: {
      fieldValue = values.outerDimensions?.width;

      if (IsNullEmptyUndefined(fieldValue)) {
        return { valid: true, warningMessage: "" };
      }

      if (values.outerDimensions?.widthUOM === "CM") {
        if (fieldValue > 115) {
          return {
            valid: false,
            warningMessage:
              "You are about to save a breakpack width dimension that is greater than the pallet width (115 CM)",
          };
        }
      }

      if (values.outerDimensions?.widthUOM === "M") {
        if (fieldValue > 1.15) {
          return {
            valid: false,
            warningMessage:
              "You are about to save a breakpack width dimension that is greater than the pallet width (1.15 M)",
          };
        }
      }
      break;
    }
    default: {
      return { valid: true, warningMessage: "" };
    }
  }
};